/**
 * The generic avatar shown when a contact has no photo: their initials on a
 * deterministic, brand-appropriate background (pure, client-safe).
 *
 * Deterministic so the same person is the same colour on every screen and every
 * visit — a colour that changed per render would read as a different person.
 * The palette is WTC-blue-led and muted (MARKETING_MINIMALISM), and every entry
 * carries WHITE text at >= 4.5:1 contrast (WCAG AA for normal text), asserted by
 * `tests/contacts/contact-avatar.test.ts` from the hex values, not by eye.
 */

/** Background hexes; each must hold white text at >= 4.5:1 (see the test). */
export const AVATAR_BACKGROUNDS = [
  "#245397", // WTC primary blue
  "#1d4f7a", // deep navy
  "#1f6173", // teal
  "#2f6049", // forest
  "#4d4a8f", // indigo
  "#6b3f7a", // plum
  "#8a3b3b", // brick
  "#7a4f1d", // bronze
  "#475569", // slate
  "#35577d", // steel
] as const;

export type AvatarBackground = (typeof AVATAR_BACKGROUNDS)[number];

/** Strips an email to its local part so `jane.doe@x.com` reads as "Jane Doe". */
function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  return local.replace(/[._+-]+/g, " ").replace(/\d+/g, " ").trim();
}

/** Words that carry a person's initials (drops honorifics, suffixes and punctuation-only tokens). */
function nameWords(name: string): string[] {
  const IGNORED = new Set(["mr", "mrs", "ms", "dr", "jr", "sr", "ii", "iii", "iv"]);
  return name
    .replace(/\([^)]*\)/g, " ")
    .split(/[\s,]+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter((w) => w.length > 0 && !IGNORED.has(w.toLowerCase()));
}

/**
 * Up to two initials: first + last word of the name ("Mary Jane Watson" → "MW"),
 * one letter for a single word, the email's local part when there is no name,
 * and "?" when there is nothing at all.
 */
export function contactInitials(name: string | null | undefined, email?: string | null): string {
  const source = (name ?? "").trim() || (email ? nameFromEmail(email) : "");
  const words = nameWords(source);
  if (words.length === 0) return "?";
  const first = Array.from(words[0])[0] ?? "";
  const last = words.length > 1 ? (Array.from(words[words.length - 1])[0] ?? "") : "";
  return (first + last).toLocaleUpperCase();
}

/** FNV-1a — a small stable string hash (the colour must not depend on runtime). */
function hash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * The background for a contact, keyed by a STABLE seed — the contact id when
 * there is one (a rename keeps the colour), else the name / email.
 */
export function avatarBackgroundFor(seed: string | null | undefined): AvatarBackground {
  const key = (seed ?? "").trim().toLowerCase();
  return AVATAR_BACKGROUNDS[hash(key) % AVATAR_BACKGROUNDS.length];
}

/** WCAG relative luminance of a `#rrggbb` colour. */
export function relativeLuminance(hex: string): number {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) throw new Error(`relativeLuminance: not a #rrggbb colour: ${hex}`);
  const channel = (h: string): number => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(m[1]) + 0.7152 * channel(m[2]) + 0.0722 * channel(m[3]);
}

/** WCAG contrast ratio of white text on `hex`. */
export function whiteContrastOn(hex: string): number {
  return 1.05 / (relativeLuminance(hex) + 0.05);
}
